import { AffiliateCouponResponse, AffiliateMeResponse } from '@avanzu/types';
import { ContentCopy, Share } from '@mui/icons-material';
import {
  Box,
  Button,
  CircularProgress,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';
import { useSnackbar } from 'notistack';
import React, { useEffect, useState } from 'react';
import PaperSection from '../../components/PaperSection';
import { apiClient } from '../../services/apiClient';
import formatDate, { DateFormatOptions } from '../../utils/formatDate';

const ReferralPage: React.FC = () => {
  const { enqueueSnackbar } = useSnackbar();
  const [me, setMe] = useState<AffiliateMeResponse['affiliate'] | null>(null);
  const [coupon, setCoupon] = useState<AffiliateCouponResponse | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchAll = async () => {
      try {
        const [meRes, couponRes] = await Promise.all([
          apiClient.get('/affiliate/me'),
          apiClient.get('/affiliate/coupon'),
        ]);
        if (meRes.ok) setMe(((await meRes.json()) as AffiliateMeResponse).affiliate);
        if (couponRes.ok) setCoupon(await couponRes.json());
      } catch (err) {
        console.error('Failed to load referral data:', err);
      } finally {
        setLoading(false);
      }
    };
    fetchAll();
  }, []);

  const copy = (text: string, label: string) => {
    navigator.clipboard.writeText(text);
    enqueueSnackbar(`${label} copied`, { variant: 'success' });
  };

  const share = async () => {
    if (!me) return;
    if (navigator.share) {
      try {
        await navigator.share({
          title: 'Avanzu',
          text: `Sign up to Avanzu with my referral link and get ${(me.discountRateBps / 100).toFixed(0)}% off your first paid project`,
          url: me.referralLink,
        });
      } catch {
        // User cancelled — nothing to do
      }
    } else {
      copy(me.referralLink, 'Referral link');
    }
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Stack spacing={3}>
      {me && (
        <PaperSection title="Your referral code">
          <Typography
            variant="h3"
            sx={{ fontFamily: 'monospace', letterSpacing: 4, mb: 2 }}
          >
            {me.referralCode}
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Share the code or the link below. Clients can also enter the code on their plan page
            after signing up.
          </Typography>
          <Typography
            variant="body2"
            sx={{
              fontFamily: 'monospace',
              bgcolor: 'action.hover',
              px: 2,
              py: 1,
              borderRadius: 1,
              mb: 2,
              overflowX: 'auto',
            }}
          >
            {me.referralLink}
          </Typography>
          <Stack direction="row" spacing={2}>
            <Button
              variant="outlined"
              startIcon={<ContentCopy />}
              onClick={() => copy(me.referralCode, 'Referral code')}
            >
              Copy code
            </Button>
            <Button
              variant="outlined"
              startIcon={<ContentCopy />}
              onClick={() => copy(me.referralLink, 'Referral link')}
            >
              Copy link
            </Button>
            <Button variant="contained" startIcon={<Share />} onClick={share}>
              Share
            </Button>
          </Stack>
        </PaperSection>
      )}

      <PaperSection title="Redemptions">
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          {coupon?.timesRedeemed ?? 0} organisation
          {(coupon?.timesRedeemed ?? 0) === 1 ? ' has' : 's have'} signed up with your code.
        </Typography>
        <TableContainer>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Organisation</TableCell>
                <TableCell>Redeemed</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {!coupon || coupon.redemptions.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={2} align="center">
                    <Typography variant="body2" color="text.secondary" sx={{ py: 3 }}>
                      No redemptions yet — share your link to get started
                    </Typography>
                  </TableCell>
                </TableRow>
              ) : (
                coupon.redemptions.map((r) => (
                  <TableRow key={r.organisationId}>
                    <TableCell>{r.orgName}</TableCell>
                    <TableCell>{formatDate(r.redeemedAt, DateFormatOptions.DATEONLY)}</TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </PaperSection>
    </Stack>
  );
};

export default ReferralPage;
